import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import {
  productionTasksAPI,
  partsAPI,
  equipmentTemplatesAPI,
  usersAPI,
} from '../api/api';
import { Modal, LoadingSpinner, EmptyState } from '../components/Common';
import TaskThread from '../components/Common/TaskThread';
import { useAuth } from '../context/AuthContext';
import { useSearch } from '../context/SearchContext';
import { useLanguage } from '../context/LanguageContext';

// Fired whenever notes change here, so the navbar bell refreshes its count
// right away instead of waiting for its next poll.
export const TASKS_CHANGED_EVENT = 'production-tasks:changed';
const notifyChanged = () => window.dispatchEvent(new Event(TASKS_CHANGED_EVENT));

const POLL_MS = 30000;

const statusColors = {
  open: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  done: 'bg-green-100 text-green-800',
};

const typeColors = {
  part: 'bg-gray-100 text-gray-700',
  component: 'bg-purple-100 text-purple-700',
  text: 'bg-orange-100 text-orange-700',
};

const emptyForm = {
  item_type: 'part',
  part_id: null,
  template_id: null,
  quantity: '',
  note: '',
  recipients: [],
};

// Render react-select menus on <body> so the modal's scroll area can't clip them.
const menuPortal = {
  menuPortalTarget: document.body,
  styles: { menuPortal: (base) => ({ ...base, zIndex: 60 }) },
};

const isLowStock = (p) => !p.is_nla && Number(p.quantity || 0) <= Number(p.min_stock_level || 0);

export const taskItemLabel = (task) => {
  if (task.item_type === 'part') return task.part_name || `#${task.part_id}`;
  if (task.item_type === 'component') return task.component_name || `#${task.template_id}`;
  return task.note || '';
};

const ProductionTasks = () => {
  const { user } = useAuth();
  const { searchTerm } = useSearch();
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateLocale = language === 'de' ? 'de-DE' : 'en-GB';
  const isAdmin = user?.userType === 'admin';

  const [scope, setScope] = useState('received');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState('');
  const [threadTaskId, setThreadTaskId] = useState(null);
  const [parts, setParts] = useState([]);
  const [components, setComponents] = useState([]);
  const [users, setUsers] = useState([]);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const { data } = await productionTasksAPI.getAll(scope);
        setTasks(data.data);
        // Opening the Received tab counts as having seen the new notes
        // (their comments only once the thread itself is opened).
        const unseen = scope === 'received' ? data.data.filter((task) => !task.read_at) : [];
        if (unseen.length > 0) {
          await Promise.all(unseen.map((task) => productionTasksAPI.markRead(task.id)));
          notifyChanged();
        }
      } catch (err) {
        if (!silent) {
          alert(t('productionTasks.failedToLoad', { error: err.response?.data?.error || err.message }));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [scope, t],
  );

  useEffect(() => {
    load();
  }, [load, location.key]);

  // Keep the list fresh while the page stays open.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') load({ silent: true });
    };
    const timer = setInterval(refresh, POLL_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  const loadFormOptions = useCallback(async () => {
    try {
      const [partsRes, componentsRes, usersRes] = await Promise.all([
        partsAPI.getAll(),
        equipmentTemplatesAPI.getComponentsStock(),
        usersAPI.getAll(),
      ]);
      setParts(partsRes.data.data || []);
      setComponents(componentsRes.data.data || []);
      setUsers(usersRes.data.data || []);
    } catch (err) {
      console.error('Failed to load form options:', err);
    }
  }, []);

  const openCreate = useCallback(
    (prefill = {}) => {
      setFormData({ ...emptyForm, ...prefill });
      setShowModal(true);
      loadFormOptions();
    },
    [loadFormOptions],
  );

  // "To produce" buttons on Parts / Component Stock link here with
  // ?new=part&id=5 (or new=component), which opens the modal prefilled.
  useEffect(() => {
    // ?open=12 (from the bell) opens that note's comment thread.
    const openId = Number(searchParams.get('open'));
    if (openId) {
      setThreadTaskId(openId);
      setSearchParams({}, { replace: true });
      return;
    }
    const type = searchParams.get('new');
    if (!type) return;
    const id = Number(searchParams.get('id')) || null;
    if (type === 'part') openCreate({ item_type: 'part', part_id: id });
    else if (type === 'component') openCreate({ item_type: 'component', template_id: id });
    else openCreate({ item_type: 'text' });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, openCreate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await productionTasksAPI.create({
        ...formData,
        recipients: formData.recipients.map((r) => r.value),
      });
      setShowModal(false);
      setFormData(emptyForm);
      setFlash(t('productionTasks.sent', { count: data.count }));
      setTimeout(() => setFlash(''), 4000);
      if (scope !== 'received') load({ silent: true });
      notifyChanged();
    } catch (err) {
      alert(t('productionTasks.failedToSend', { error: err.response?.data?.error || err.message }));
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (task, status) => {
    try {
      const { data } = await productionTasksAPI.updateStatus(task.id, status);
      setTasks((prev) => prev.map((x) => (x.id === task.id ? data.data : x)));
      notifyChanged();
    } catch (err) {
      alert(t('productionTasks.failedToUpdate', { error: err.response?.data?.error || err.message }));
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(t('productionTasks.confirmDelete'))) return;
    try {
      await productionTasksAPI.delete(task.id);
      setTasks((prev) => prev.filter((x) => x.id !== task.id));
      notifyChanged();
    } catch (err) {
      alert(t('productionTasks.failedToUpdate', { error: err.response?.data?.error || err.message }));
    }
  };

  const term = searchTerm.toLowerCase();
  const filtered = tasks.filter((task) => {
    if (!showDone && task.status === 'done') return false;
    if (!term) return true;
    return [
      taskItemLabel(task),
      task.note,
      task.created_by_name,
      task.assigned_to_name,
      task.part_article_id,
      task.component_article_id,
    ].some((v) => v && String(v).toLowerCase().includes(term));
  });

  // Low stock parts first, then alphabetical; NLA parts can't be produced.
  const partOptions = parts
    .filter((p) => !p.is_nla)
    .sort((a, b) => isLowStock(b) - isLowStock(a) || a.name.localeCompare(b.name))
    .map((p) => ({
      value: p.id,
      label: `${p.name}${p.article_id ? ` (${p.article_id})` : ''}${
        isLowStock(p) ? ` — ⚠ ${t('productionTasks.lowStockTag')}` : ''
      }`,
      part: p,
    }));
  const componentOptions = components.map((c) => ({
    value: c.template_id,
    label: `${c.name}${c.brand ? ` — ${c.brand}` : ''}`,
    component: c,
  }));
  const userOptions = users
    .filter((u) => u.id !== user?.id)
    .map((u) => ({ value: u.id, label: u.full_name || u.loginName || `#${u.id}` }));

  const selectedPart = partOptions.find((o) => o.value === formData.part_id) || null;
  const selectedComponent = componentOptions.find((o) => o.value === formData.template_id) || null;

  const tabs = [
    { key: 'received', label: t('productionTasks.tabReceived') },
    { key: 'sent', label: t('productionTasks.tabSent') },
    ...(isAdmin ? [{ key: 'all', label: t('productionTasks.tabAll') }] : []),
  ];

  return (
    <div>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>{t('productionTasks.title')}</h1>
          <p className='text-gray-600 mt-1'>{t('productionTasks.subtitle')}</p>
        </div>
        <button onClick={() => openCreate()} className='btn-primary w-full sm:w-auto whitespace-nowrap'>
          {t('productionTasks.newTask')}
        </button>
      </div>

      {flash && (
        <div className='mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm'>
          ✓ {flash}
        </div>
      )}

      <div className='card mb-6 flex flex-wrap items-center justify-between gap-3'>
        <div className='flex rounded-lg border border-gray-300 overflow-hidden'>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setScope(tab.key)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                scope === tab.key ? 'bg-[#361826] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <label className='flex items-center gap-2 text-sm text-gray-600'>
          <input type='checkbox' checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
          {t('productionTasks.showDone')}
        </label>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon='📝'
          title={t('productionTasks.emptyTitle')}
          message={t('productionTasks.emptyMessage')}
          actionLabel={t('productionTasks.newTask')}
          onAction={() => openCreate()}
          hasItems={tasks.length > 0}
        />
      ) : (
        <div className='bg-white rounded-lg shadow overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('productionTasks.colItem')}</th>
                <th className='px-4 py-2 text-center text-xs font-medium text-gray-500'>{t('productionTasks.colQuantity')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('productionTasks.colFrom')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('productionTasks.colTo')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('productionTasks.colNote')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('productionTasks.colStatus')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('productionTasks.colDate')}</th>
                <th className='px-4 py-2 text-right text-xs font-medium text-gray-500'>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {filtered.map((task) => {
                const isNew = scope === 'received' && !task.read_at;
                const canDelete = isAdmin || task.created_by === user?.id;
                return (
                  <tr key={task.id} className={`hover:bg-gray-50 ${task.status === 'done' ? 'opacity-60' : ''}`}>
                    <td className='px-4 py-3 text-sm'>
                      <div className='flex items-center gap-2'>
                        <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${typeColors[task.item_type]}`}>
                          {t(`productionTasks.type_${task.item_type}`)}
                        </span>
                        {isNew && (
                          <span className='px-2 py-0.5 text-xs rounded-full bg-red-500 text-white'>
                            {t('productionTasks.newBadge')}
                          </span>
                        )}
                      </div>
                      {task.item_type === 'part' && (
                        <button
                          onClick={() => navigate('/parts')}
                          className='mt-1 font-medium text-blue-600 hover:underline text-left'
                        >
                          {task.part_name}
                        </button>
                      )}
                      {task.item_type === 'component' && (
                        <button
                          onClick={() => navigate('/component-stock')}
                          className='mt-1 font-medium text-blue-600 hover:underline text-left'
                        >
                          {task.component_name}
                        </button>
                      )}
                      {task.item_type === 'part' && task.part_quantity !== null && (
                        <p className='text-xs text-gray-500'>
                          {t('productionTasks.stockInfo', {
                            qty: task.part_quantity,
                            min: task.part_min_stock_level,
                          })}
                        </p>
                      )}
                      {task.item_type === 'component' && task.component_stock !== null && (
                        <p className='text-xs text-gray-500'>
                          {t('productionTasks.componentStockInfo', { qty: task.component_stock })}
                        </p>
                      )}
                    </td>
                    <td className='px-4 py-3 text-center text-sm font-semibold text-gray-900'>
                      {task.quantity ?? '-'}
                    </td>
                    <td className='px-4 py-3 text-sm text-gray-700 whitespace-nowrap'>{task.created_by_name || '-'}</td>
                    <td className='px-4 py-3 text-sm text-gray-700 whitespace-nowrap'>{task.assigned_to_name || '-'}</td>
                    <td className='px-4 py-3 text-sm text-gray-600 max-w-xs whitespace-pre-wrap'>{task.note || '-'}</td>
                    <td className='px-4 py-3 text-sm'>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${statusColors[task.status]}`}>
                        {t(`productionTasks.status_${task.status}`)}
                      </span>
                      {task.status === 'done' && task.done_by_name && (
                        <p className='text-xs text-gray-500 mt-1'>
                          {t('productionTasks.doneBy', { name: task.done_by_name })}
                        </p>
                      )}
                    </td>
                    <td className='px-4 py-3 text-xs text-gray-500 whitespace-nowrap'>
                      {new Date(task.created_at).toLocaleString(dateLocale)}
                    </td>
                    <td className='px-4 py-3 text-xs text-right whitespace-nowrap space-x-3'>
                      <button
                        onClick={() => setThreadTaskId(task.id)}
                        className='relative text-gray-700 hover:text-gray-900'
                        title={t('productionTasks.comments')}
                      >
                        💬 {task.comment_count || 0}
                        {task.unread_comments > 0 && (
                          <span className='absolute -top-1 -right-2 w-2 h-2 rounded-full bg-red-500' />
                        )}
                      </button>
                      {task.status === 'open' && (
                        <button onClick={() => changeStatus(task, 'in_progress')} className='text-blue-600 hover:text-blue-900'>
                          {t('productionTasks.start')}
                        </button>
                      )}
                      {task.status !== 'done' ? (
                        <button onClick={() => changeStatus(task, 'done')} className='text-green-600 hover:text-green-900'>
                          {t('productionTasks.markDone')}
                        </button>
                      ) : (
                        <button onClick={() => changeStatus(task, 'open')} className='text-gray-600 hover:text-gray-900'>
                          {t('productionTasks.reopen')}
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(task)} className='text-red-600 hover:text-red-900'>
                          {t('common.delete')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <TaskThread
        taskId={threadTaskId}
        onClose={() => {
          setThreadTaskId(null);
          load({ silent: true });
        }}
        onChanged={notifyChanged}
      />

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={t('productionTasks.newTask').replace('+ ', '')}>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('productionTasks.itemType')}</label>
            <div className='flex flex-wrap gap-2'>
              {['part', 'component', 'text'].map((type) => (
                <button
                  type='button'
                  key={type}
                  onClick={() => setFormData({ ...formData, item_type: type })}
                  className={`px-3 py-2 text-sm rounded-lg border ${
                    formData.item_type === type
                      ? 'bg-[#361826] text-white border-[#361826]'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t(`productionTasks.type_${type}`)}
                </button>
              ))}
            </div>
          </div>

          {formData.item_type === 'part' && (
            <div>
              <Select
                classNamePrefix='react-select'
                {...menuPortal}
                options={partOptions}
                value={selectedPart}
                onChange={(opt) => setFormData({ ...formData, part_id: opt ? opt.value : null })}
                placeholder={t('productionTasks.selectPart')}
                isClearable
              />
              <p className='text-xs text-gray-500 mt-1'>
                {selectedPart
                  ? t('productionTasks.stockInfo', {
                      qty: selectedPart.part.quantity ?? 0,
                      min: selectedPart.part.min_stock_level ?? 0,
                    })
                  : t('productionTasks.lowStockFirst')}
              </p>
            </div>
          )}

          {formData.item_type === 'component' && (
            <div>
              <Select
                classNamePrefix='react-select'
                {...menuPortal}
                options={componentOptions}
                value={selectedComponent}
                onChange={(opt) => setFormData({ ...formData, template_id: opt ? opt.value : null })}
                placeholder={t('productionTasks.selectComponent')}
                isClearable
              />
              {selectedComponent && (
                <p className='text-xs text-gray-500 mt-1'>
                  {t('productionTasks.componentStockInfo', { qty: selectedComponent.component.component_stock })}
                </p>
              )}
            </div>
          )}

          {formData.item_type !== 'text' && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('productionTasks.quantity')}</label>
              <input
                type='number'
                min='1'
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className='input-field w-32'
              />
            </div>
          )}

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('productionTasks.note')}</label>
            <textarea
              rows={3}
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              className='input-field'
              placeholder={t('productionTasks.notePlaceholder')}
              required={formData.item_type === 'text'}
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('productionTasks.recipients')}</label>
            <Select
              classNamePrefix='react-select'
              {...menuPortal}
              isMulti
              options={userOptions}
              value={formData.recipients}
              onChange={(opts) => setFormData({ ...formData, recipients: opts || [] })}
              placeholder={t('productionTasks.selectRecipients')}
            />
          </div>

          <div className='flex justify-end gap-3 pt-2'>
            <button type='button' onClick={() => setShowModal(false)} className='btn-secondary'>
              {t('common.cancel')}
            </button>
            <button
              type='submit'
              className='btn-primary'
              disabled={
                sending ||
                formData.recipients.length === 0 ||
                (formData.item_type === 'part' && !formData.part_id) ||
                (formData.item_type === 'component' && !formData.template_id)
              }
            >
              {sending ? t('productionTasks.sending') : t('productionTasks.send')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ProductionTasks;
